<template>
  <div class="single-input-money">
    <SingleInput
      v-model="moneyValueFormatted"
      type="number"
      placeholder="0,00"
      prefix="R$"
      show-clear-button
      :status="status"
      :helper-text="helperText"
      @change="handleChange"
      @on-clear="onClear"
    />
    <ShortcutValue
      v-if="hasShortcut"
      class="single-input-money__shortcut"
      :values="shortcutValue"
      @click="shortcutClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { VMoney } from 'v-money';

import { SingleInput, ShortcutValue } from '@warrenbrasil/nebraska-web';

import { currencyToNumber } from '@/modules/common/helpers/currency';
import { IShortcutValue, EShortcutType } from './types';

interface Props {
  shortcutValue?: IShortcutValue[] | null;
  hasShortcut?: boolean;
  status?: string;
  helperText?: string;
  value?: string;
  hasFraction?: boolean;
}

interface EmitEvents {
  (event: 'change', data: { value: number; formatted: string }): void;
}

const emit = defineEmits<EmitEvents>();

const { value, hasFraction } = withDefaults(defineProps<Props>(), {
  shortcutValue: null,
  hasShortcut: false,
  status: '',
  helperText: '',
  value: '',
  hasFraction: true
});

const moneyValueFormatted = ref(value);

const moneyValue = computed(() => currencyToNumber(moneyValueFormatted.value));

function handleChange(event: Event) {
  const target = event.target as HTMLInputElement;
  moneyValueFormatted.value = target.value.replace('-', '');

  emitChange();
}

function onClear() {
  const value = hasFraction ? '0,00' : '0';
  moneyValueFormatted.value = value;

  emitChange();
}

function shortcutClick(data: IShortcutValue) {
  const options = { minimumFractionDigits: hasFraction ? 2 : 0 };
  const numberFormat = new Intl.NumberFormat('pt-BR', options);

  if (data.type === EShortcutType.SET) {
    moneyValueFormatted.value = numberFormat.format(data.value);
  } else {
    moneyValueFormatted.value = numberFormat.format(
      moneyValue.value + data.value
    );
  }

  emitChange();
}

function emitChange() {
  const data = {
    value: moneyValue.value,
    formatted: moneyValueFormatted.value
  };

  emit('change', data);
}

onMounted(() => {
  const textarea = document.querySelector('textarea') as HTMLElement;

  VMoney(textarea, {
    value: {
      decimal: ',',
      thousands: '.',
      prefix: '',
      precision: hasFraction ? 2 : 0
    }
  });
});
</script>

<style lang="less" scoped>
.single-input-money {
  &__shortcut {
    margin-top: @size-spacing-x400;
  }
}
</style>
